import { useState } from "react";
import { installSteps } from "@/mocks/home";

export default function InstallGuide() {
  const [active, setActive] = useState(0);
  const step = installSteps[active];

  return (
    <section id="install" className="w-full bg-background-100 py-14 md:py-24">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16">
          <span className="inline-block font-heading tracking-[0.32em] text-xs font-semibold text-primary-600">
            EASY ASSEMBLY MANUAL
          </span>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight tracking-tight">
            X9 PRO 설치방법
          </h2>
          <p className="mt-4 text-foreground-700 leading-relaxed">
            전문 엔지니어의 도움 없이도 단 10분 만에 헬멧에 완벽히 피팅할 수 있습니다.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-stretch">
          <div className="lg:col-span-5 flex flex-col justify-center space-y-3">
            {installSteps.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(i)}
                className={`w-full min-w-0 text-left p-4 sm:p-5 rounded-xl border transition-all duration-300 flex items-center gap-3 sm:gap-4 cursor-pointer relative overflow-hidden ${
                  i === active
                    ? "bg-background-50 border-primary-400 shadow-md"
                    : "bg-background-50/50 border-background-200/50 hover:border-background-300/60"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl shrink-0 flex flex-col items-center justify-center font-heading font-black transition-colors duration-300 ${
                    i === active
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200 text-foreground-400"
                  }`}
                >
                  <span className="text-[9px] font-medium leading-none">STEP</span>
                  <span className="text-lg leading-none">{String(s.id).padStart(2, "0")}</span>
                </div>
                <div className="min-w-0 grow">
                  <h3
                    className={`text-sm md:text-base font-bold break-keep transition-colors duration-300 ${
                      i === active ? "text-primary-600" : "text-foreground-950"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <p className="text-[11px] leading-relaxed text-foreground-500 line-clamp-2 break-keep mt-0.5">
                    {s.desc}
                  </p>
                </div>
                {i === active && (
                  <div className="absolute left-0 top-1/4 bottom-1/4 w-[3px] bg-primary-500 rounded-r-full"></div>
                )}
              </button>
            ))}
          </div>

          <div className="lg:col-span-7 flex">
            <div className="w-full bg-background-50 border border-background-200/70 rounded-2xl p-4 sm:p-6 md:p-8 flex flex-col justify-between shadow-sm relative overflow-hidden">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-primary-100 border border-primary-200 rounded-xl text-primary-600">
                    <i className="ri-tools-line text-lg"></i>
                  </div>
                  <div>
                    <span className="text-[10px] font-heading font-bold text-secondary-600 uppercase tracking-widest block">
                      DETAILED INSTRUCTIONS · STEP {String(step.id).padStart(2, "0")}
                    </span>
                    <h4 className="text-xl font-bold text-foreground-950 font-heading mt-0.5">
                      {step.title}
                    </h4>
                  </div>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">{step.desc}</p>

                <a
                  href={step.image}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${step.title} 설치 이미지 크게 보기`}
                  className="group relative block rounded-xl overflow-hidden border border-background-200/50 bg-white p-1.5 sm:p-2 shadow-sm cursor-zoom-in"
                >
                  {step.id === 3 && (
                    <div className="pl-[4.2%] pr-3 pt-2 pb-1.5 sm:pt-2.5 sm:pb-2 text-left font-heading font-bold text-[#e98200] leading-tight whitespace-nowrap">
                      <span className="text-[12px] sm:text-[16px] md:text-[21px]">풀페이스 헬멧 설치</span>
                      <span className="ml-1 sm:ml-1.5 text-[8px] sm:text-[10px] md:text-[12px] font-medium tracking-wide">
                        (Full-face Helmet Installation)
                      </span>
                    </div>
                  )}
                  <img
                    src={step.image}
                    alt={step.title}
                    className="block w-full h-auto object-contain rounded-lg"
                  />
                  <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/80 px-3 py-2 text-[11px] font-bold text-white shadow-lg md:hidden">
                    <i className="ri-zoom-in-line"></i>
                    눌러서 확대
                  </span>
                </a>

                <div className="border-t border-background-200/70 pt-5">
                  <h5 className="text-xs font-bold text-foreground-950 uppercase tracking-widest mb-3 flex items-center gap-1.5 font-heading">
                    <i className="ri-checkbox-circle-line text-primary-500"></i>
                    장착 시 필수 체크 포인트
                  </h5>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {step.tips.map((tip) => (
                      <li
                        key={tip}
                        className="flex items-center gap-2.5 text-xs text-foreground-800 bg-background-100 border border-background-200/50 px-3.5 py-2.5 rounded-xl"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0"></span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-between pt-6 border-t border-background-200/50 mt-6">
                <button
                  type="button"
                  disabled={active === 0}
                  onClick={() => setActive(active - 1)}
                  className="text-xs font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer disabled:text-foreground-300 disabled:cursor-not-allowed text-foreground-700 hover:bg-background-100"
                >
                  이전 단계
                </button>
                <div className="flex gap-1.5">
                  {installSteps.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === active ? "w-6 bg-primary-500" : "w-1.5 bg-background-300"
                      }`}
                    ></div>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={active === installSteps.length - 1}
                  onClick={() => setActive(active + 1)}
                  className="text-xs font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer disabled:text-foreground-300 disabled:cursor-not-allowed text-primary-600 hover:bg-primary-50"
                >
                  다음 단계
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
